import { css, SerializedStyles } from '@emotion/react';
import React from 'react';

import { useMobile } from 'MobileContext';

type Card = any;
export type HoverCardRenderer = (args: { card: Card }) => React.ReactElement;
export type HoverCardListener = (card: Card | null) => void;

// Offset means it is offset from the logview
export type HoverCardPosition =
  | 'bottom-right-offset'
  | 'bottom-right'
  | 'top-right-offset'
  | 'center'
  | 'tutorial'
  | 'tutorial-top';

class HoverCardStore {
  private hoveredCard_: Card | null = null;
  private listeners_: HoverCardListener[] = [];
  private position_: HoverCardPosition = 'bottom-right';
  private renderer_: HoverCardRenderer | null = null;

  private lockedData_: {
    card: Card;
    renderer: HoverCardRenderer;
    position: HoverCardPosition;
  } | null = null;

  constructor() {}

  // a listener is a function of 1 argument, a Card
  addListener(listener: HoverCardListener): void {
    this.listeners_.push(listener);
    listener(null);
  }
  removeListener(listener: HoverCardListener): void {
    this.listeners_ = this.listeners_.filter((x) => x !== listener);
  }
  getHoverCardComponent(): React.ReactElement {
    return <HoverCard store={this} />;
  }
  setCard(card: Card | null, renderer: HoverCardRenderer | null): void {
    this.hoveredCard_ = card;
    this.renderer_ = renderer;
    this.notifyListeners_();
  }
  setTutorialCard(
    card: Card,
    renderer: HoverCardRenderer,
    position?: HoverCardPosition,
  ): void {
    this.lockedData_ = { card, renderer, position: position || 'tutorial' };
    this.notifyListeners_();
  }
  clearTutorialCard(): void {
    this.lockedData_ = null;
    this.notifyListeners_();
  }
  setPosition(position: HoverCardPosition): void {
    this.position_ = position;
  }
  getPosition(): HoverCardPosition {
    return this.lockedData_?.position || this.position_;
  }
  getHoveredCard(): Card | null {
    return this.lockedData_?.card || this.hoveredCard_;
  }
  getRenderer(): HoverCardRenderer | null {
    return this.lockedData_?.renderer || this.renderer_;
  }

  // internal
  notifyListeners_(): void {
    this.listeners_.forEach((listener) => {
      listener(this.hoveredCard_);
    });
  }
}

interface Props {
  store: HoverCardStore;
}
const HoverCard: React.FC<Props> = ({ store }) => {
  const [, forceUpdate] = React.useState({});
  const isMobile = useMobile();

  const onHoverCardChange_ = React.useCallback(() => {
    forceUpdate({});
  }, []);

  React.useEffect(() => {
    store.addListener(onHoverCardChange_);
    return () => {
      store.removeListener(onHoverCardChange_);
    };
  }, [store, onHoverCardChange_]);
  // card is 83 x 110px @ 1x
  const card = store.getHoveredCard();
  const renderer = store.getRenderer();
  const rendered_card = card && renderer ? renderer({ card: card }) : null;

  if (isMobile || matchMedia('(hover: none)').matches) {
    const style = {
      transform: 'translate(-50%, -50%)',
      left: '50%',
      top: '50%',
      zoom: '2.5',
    };
    return (
      <div css={HoverCardStyles.hoverCard} style={style}>
        {rendered_card}
      </div>
    );
  }

  return (
    <div
      css={[
        HoverCardStyles.hoverCard,
        HoverCardPositionStyles[store.getPosition()],
      ]}
    >
      {rendered_card}
    </div>
  );
};

const HoverCardStyles = {
  hoverCard: css({
    position: 'fixed',
    zIndex: 1000,
    pointerEvents: 'none',
  }),
};
const HoverCardPositionStyles: Record<HoverCardPosition, SerializedStyles> = {
  'bottom-right-offset': css({
    bottom: '25%',
    right: 450,
    transform: `scale(2.5)`,
  }),
  'bottom-right': css({
    right: '9%',
    bottom: '25%',
    transform: `scale(2.5)`,
  }),
  'top-right-offset': css({
    transform: `translate(calc(100dvw - 440px), 20px) translate(50%, -50%) scale(2.5) translate(-50%, 50%)`,
  }),
  center: css({
    transform: `translate(-50%, -50%) scale(2.5)`,
    left: '50%',
    top: '50%',
  }),
  tutorial: css({
    transform: `scale(2.5)`,
    left: 200,
    top: 300,
  }),
  'tutorial-top': css({
    transform: `scale(2.5)`,
    left: 200,
    top: 100,
  }),
};

export default new HoverCardStore();
